import { Injectable } from '@angular/core';
import { BehaviorSubject, forkJoin, Observable, of } from 'rxjs';
import { IChainMenu, IChainMenuGroup, IMenu, IMenuGroup, MenuFile } from '../../interfaces/menu';
import { MenuService } from './menu.service';
import { map, shareReplay, switchMap, tap } from 'rxjs/operators';
import { FileUploadService } from '../file-upload.service';

interface IMenuState {
  menus: IMenu[];
  chainMenus: IChainMenu[];
  loader: boolean;
  menuPage?: number;
  menuType?: string;
  chainMenuType?: string;
}

@Injectable({
  providedIn: 'root',
})
export class MenuStoreService {
  constructor(
    private menuService: MenuService,
    private fileUpload: FileUploadService,
  ) {}

  private state$ = new BehaviorSubject<IMenuState>({
    menus: [],
    chainMenus: [],
    loader: false,
  });

  public fetchMenus({
    restaurantId,
    sort,
    order,
    menuType,
  }: {
    restaurantId: number;
    sort?: string;
    order?: string;
    menuType?: string;
  }): void {
    this.toggleLoader(true);

    this.menuService.getRestaurantMenus({ restaurantId, sort, order, menuType }).subscribe((result) => {
      const state = this.state$.getValue();
      this.state$.next({
        ...state,
        menus: result,
        loader: false,
        menuType: menuType || '',
      });
    }, () => {
      this.toggleLoader(false);
    });
  }

  public fetchMenu(restaurantId: number, menuId: number): void {
    this.toggleLoader(true);

    this.menuService.getRestaurantMenuById(restaurantId, menuId).subscribe({
      next: (result) => {
        const state = this.state$.getValue();

        const existingMenu = state.menus.find(menu => menu.id === result.id);
        const shouldIncludeMenu = this.shouldIncludeMenu(result, state.menuType);

        let updatedMenus;

        if (existingMenu) {
          updatedMenus = shouldIncludeMenu
            ? state.menus.map(menu => menu.id === result.id ? result : menu)
            : state.menus.filter(menu => menu.id !== result.id);
        } else {
          updatedMenus = shouldIncludeMenu ? [result, ...state.menus] : state.menus;
        }

        this.state$.next({
          ...state,
          menus: updatedMenus,
          loader: false,
        });
      },
      error: () => {
        this.toggleLoader(false);
      }
    });
  }

  public addMenu(
    restaurantId: number,
    name: string,
    language: string | null,
    happyHours: boolean,
    hidden: boolean,
    file: File,
    originalId: number | null,
    iconFile?: File,
    bannerFile?: File
  ): Observable<IMenu> {
    const menuFileUpload$: Observable<string> = this.fileUpload.upload(file).pipe(
      map(event => event.intent.fileId.toString())
    );

    let iconFileUpload$: Observable<string | null> = of(null);
    let bannerFileUpload$: Observable<string | null> = of(null);

    if (iconFile) {
      iconFileUpload$ = this.fileUpload.upload(iconFile).pipe(
        map(event => event.intent.fileId.toString())
      );
    }

    if (bannerFile) {
      bannerFileUpload$ = this.fileUpload.upload(bannerFile).pipe(
        map(event => event.intent.fileId.toString())
      );
    }

    return forkJoin([menuFileUpload$, iconFileUpload$, bannerFileUpload$]).pipe(
      switchMap(([menuFileId, iconFileId, bannerFileId]) => {
        return this.menuService.addMenu(
          restaurantId,
          menuFileId,
          name,
          language,
          happyHours,
          hidden,
          originalId,
          iconFileId,
          bannerFileId
        );
      }),
      tap((newItem) => {
        const state = this.state$.getValue();
        const menus = [newItem, ...state.menus];

        this.state$.next({
          ...state,
          menus,
        });
      })
    );
  }

  public updateMenu(
    menuId: number,
    restaurantId: number,
    data: {
      name: string;
      language: string | null;
      hidden: boolean;
      menuFile: File | MenuFile | null;
      happyHours: boolean;
      iconFile: File | string | null;
      bannerFile: File | string | null;
    }
  ): Observable<IMenu> {
    let menuFileUpload$: Observable<string | null> = of(null);
    let iconFileUpload$: Observable<string | null> = of(null);
    let bannerFileUpload$: Observable<string | null> = of(null);

    if (data.menuFile instanceof File && data.menuFile.size) {
      menuFileUpload$ = this.fileUpload.upload(data.menuFile).pipe(
        map(event => event.intent.fileId.toString())
      );
    }

    if (data.iconFile instanceof File && data.iconFile.size) {
      iconFileUpload$ = this.fileUpload.upload(data.iconFile).pipe(
        map(event => event.intent.fileId.toString())
      );
    }

    if (data.bannerFile instanceof File && data.bannerFile.size) {
      bannerFileUpload$ = this.fileUpload.upload(data.bannerFile).pipe(
        map(event => event.intent.fileId.toString())
      );
    }

    return forkJoin([menuFileUpload$, iconFileUpload$, bannerFileUpload$]).pipe(
      switchMap(([menuFileId, iconFileId, bannerFileId]) => {
        return this.menuService.updateMenu(
          restaurantId,
          menuId,
          data.name,
          data.language,
          data.happyHours,
          data.hidden,
          menuFileId,
          iconFileId,
          bannerFileId
        ).pipe(
          tap((updatedMenu) => {
            const state = this.state$.getValue();
            const shouldIncludeMenu = this.shouldIncludeMenu(updatedMenu, state.menuType);
            const existingMenu = state.menus.find(menu => menu.id === updatedMenu.id);
            let updatedMenus: IMenu[];

            if (existingMenu) {
              updatedMenus = shouldIncludeMenu
                ? state.menus.map(menu => menu.id === updatedMenu.id ? updatedMenu : menu)
                : state.menus.filter(menu => menu.id !== updatedMenu.id);
            } else {
              updatedMenus = shouldIncludeMenu ? [updatedMenu, ...state.menus] : state.menus;
            }

            this.state$.next({
              ...state,
              menus: updatedMenus,
            });
          })
        );
      }),
    );
  }

  public toggleLoader(value: boolean): void {
    const state = this.state$.getValue();
    this.state$.next({
      ...state,
      loader: value,
    });
  }

  get getMenus$(): Observable<IMenuGroup[]> {
    return this.state$.asObservable().pipe(
      map((state) => state.menus),
      map(menus => {
        return [...menus]
          .sort((a, b) => (a.ownerType === 'CHAIN' ? -1 : 1))
          .filter((menu) => !menu.originalId)
          .map((item) => ({
            ...item,
            translation: menus.filter((menu) => menu.originalId === item.id)
          })) as IMenuGroup[];
      }),
    );
  }

  get getChainMenus$(): Observable<IChainMenuGroup[]> {
    return this.state$.pipe(
      map((state) => state.chainMenus),
      map(menus => {
        return menus.filter((menu) => !menu.originalId).map((item) => {
          return {
            ...item,
            translation: menus.filter((menu) => menu.originalId === item.id)
          };
        }) as IChainMenuGroup[];
      }),
      shareReplay(1)
    );
  }

  get isLoading$(): Observable<boolean> {
    return this.state$.asObservable().pipe(
      map((state) => state.loader)
    );
  }

  public fetchChainMenus({
    chainId,
    sort,
    order,
    menuType,
  }: {
    chainId: number;
    sort?: string;
    order?: string;
    menuType?: string;
  }): void {
    this.toggleLoader(true);

    this.menuService.getChainMenus({ chainId, sort, order, menuType }).subscribe((result) => {
      const state = this.state$.getValue();
      this.state$.next({
        ...state,
        chainMenus: result,
        loader: false,
        chainMenuType: menuType || '',
      });
    }, () => {
      this.toggleLoader(false);
    });
  }

  public fetchChainMenu(chainId: number, menuId: number): void {
    this.toggleLoader(true);

    this.menuService.getChainMenuById(chainId, menuId).subscribe({
      next: (result) => {
        const state = this.state$.getValue();
        const existingMenu = state.chainMenus.find(menu => menu.id === result.id);
        const shouldIncludeMenu = this.shouldIncludeMenu(result, state.chainMenuType);

        let updatedChainMenus;

        if (existingMenu) {
          updatedChainMenus = shouldIncludeMenu
            ? state.chainMenus.map(menu => menu.id === result.id ? result : menu)
            : state.chainMenus.filter(menu => menu.id !== result.id);
        } else {
          updatedChainMenus = shouldIncludeMenu ? [result, ...state.chainMenus] : state.chainMenus;
        }

        this.state$.next({
          ...state,
          chainMenus: updatedChainMenus,
          loader: false,
        });
      },
      error: () => {
        this.toggleLoader(false);
      }
    });
  }

  public addChainMenu(
    chainId: number,
    name: string,
    language: string | null,
    happyHours: boolean,
    hidden: boolean,
    file: File,
    selectedRestaurants: number[] | null,
    originalId: number | null,
    iconFile?: File,
    bannerFile?: File
  ): Observable<IChainMenu> {
    const menuChainFileUpload$: Observable<string> = this.fileUpload.upload(file).pipe(
      map(event => event.intent.fileId.toString())
    );

    let iconFileUpload$: Observable<string | null> = of(null);
    let bannerFileUpload$: Observable<string | null> = of(null);

    if (iconFile) {
      iconFileUpload$ = this.fileUpload.upload(iconFile).pipe(
        map(event => event.intent.fileId.toString())
      );
    }

    if (bannerFile) {
      bannerFileUpload$ = this.fileUpload.upload(bannerFile).pipe(
        map(event => event.intent.fileId.toString())
      );
    }
    return forkJoin([menuChainFileUpload$, iconFileUpload$, bannerFileUpload$]).pipe(
      switchMap(([menuFileId, iconFileId, bannerFileId]) => {
        return this.menuService.addChainMenu(
          chainId,
          menuFileId,
          name,
          language,
          happyHours,
          hidden,
          originalId,
          selectedRestaurants,
          iconFileId,
          bannerFileId
        );
      }),
      tap((newItem) => {
        const state = this.state$.getValue();
        const chainMenus = [newItem, ...state.chainMenus];

        this.state$.next({
          ...state,
          chainMenus,
        });
      })
    );
  }

  public updateChainMenu(
    menuId: number,
    chainId: number,
    selectedRestaurants: number[] | null,
    data: {
      name: string;
      language: string | null;
      happyHours: boolean,
      hidden: boolean;
      menuFile: File | MenuFile | null;
      iconFile?: File | string | null;
      bannerFile?: File | string | null;
    }
  ): Observable<IChainMenu> {
    let menuChainFileUpload$: Observable<string | null> = of(null);
    let iconFileUpload$: Observable<string | null> = of(null);
    let bannerFileUpload$: Observable<string | null> = of(null);

    if (data.menuFile instanceof File && data.menuFile.size) {
      menuChainFileUpload$ = this.fileUpload.upload(data.menuFile).pipe(
        map(event => event.intent.fileId.toString())
      );
    }

    if (data.iconFile instanceof File && data.iconFile.size) {
      iconFileUpload$ = this.fileUpload.upload(data.iconFile).pipe(
        map(event => event.intent.fileId.toString())
      );
    }

    if (data.bannerFile instanceof File && data.bannerFile.size) {
      bannerFileUpload$ = this.fileUpload.upload(data.bannerFile).pipe(
        map(event => event.intent.fileId.toString())
      );
    }

    return forkJoin([menuChainFileUpload$, iconFileUpload$, bannerFileUpload$]).pipe(
      switchMap(([menuFileId, iconFileId, bannerFileId]) => {
        return this.menuService.updateChainMenu(
          menuId,
          chainId,
          data.name,
          data.language,
          data.happyHours,
          data.hidden,
          selectedRestaurants,
          menuFileId,
          iconFileId,
          bannerFileId
        ).pipe(
          tap((updatedMenu) => {
            const state = this.state$.getValue();
            const shouldIncludeMenu = this.shouldIncludeMenu(updatedMenu, state.chainMenuType);
            const existingMenu = state.chainMenus.find(menu => menu.id === updatedMenu.id);
            let updatedChainMenus: IChainMenu[];

            if (existingMenu) {
              updatedChainMenus = shouldIncludeMenu
                ? state.chainMenus.map(menu => menu.id === updatedMenu.id ? updatedMenu : menu)
                : state.chainMenus.filter(menu => menu.id !== updatedMenu.id);
            } else {
              updatedChainMenus = shouldIncludeMenu ? [updatedMenu, ...state.chainMenus] : state.chainMenus;
            }

            this.state$.next({
              ...state,
              chainMenus: updatedChainMenus,
            });
          })
        );
      })
    );
  }

  public removeRestaurantsMenu(restaurantsId: number, menuId: number): Observable<{ message: string }> {
    return this.menuService.deleteRestaurantMenu(restaurantsId, menuId).pipe(
      tap(() => {
        const state = this.state$.getValue();
        const newArr = state.menus.filter((menu) => menu.id !== menuId);
        this.state$.next({
          ...state,
          menus: newArr,
        });
      })
    );
  }

  public removeChainMenu(chainId: number, menuId: number): Observable<{ message: string }> {
    return this.menuService.deleteChainMenu(chainId, menuId).pipe(
      tap(() => {
        const state = this.state$.getValue();
        const newArr = state.chainMenus.filter((chainMenus) => chainMenus.id !== menuId);
        this.state$.next({
          ...state,
          chainMenus: newArr,
        });
      })
    );
  }

  private shouldIncludeMenu(menu: Pick<IMenu, 'happyHours'>, menuType?: string): boolean {
    if (menuType === 'mainMenu') {
      return !menu.happyHours;
    }

    if (menuType === 'happyMenu') {
      return menu.happyHours;
    }

    return true;
  }
}
